import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Tooltip } from "react-tooltip";
import { getNotificationChannels } from "../../modules/Notification";
import { showSpinner, closeSpinner, modal, alert } from "../../modules/Dialogs";
import { sessionExpired } from "../../config/GeneralConfig";
import { toScreen } from "../../modules/Routing";
import ChannelDetails from "./ChannelDetails";

const COLUMNS = [
  { key: "activityKey", label: "Activity Key", class: "c-list-entry__mavname", sortable: false },
  { key: "channel",     label: "Channel",       class: "c-list-entry__rating",  sortable: false },
  { key: "description", label: "Description",   class: "c-list-entry__reason",  sortable: false },
  { key: "type",        label: "Type",           class: "c-list-entry__status",  sortable: false },
  { key: "status",      label: "Status",         class: "c-list-entry__startDate", sortable: false },
];

const CHANNEL_BADGE = {
  email: "badge-orange",
  push: "badge-info",
};

const PAGE_SIZE = 15;

const ChannelsTab = ({ userDetails, navigate }) => {
  const [data, setData]                     = useState([]);
  const [inputSearchValue, setInputSearchValue] = useState("");
  const [searchValue, setSearchValue]       = useState("");
  const [sort, setSort]                     = useState("createdAt=-1");
  const [nextCursor, setNextCursor]         = useState(null);
  const [hasNextPage, setHasNextPage]       = useState(false);
  const [cursorStack, setCursorStack]       = useState([null]);
  const [isLoadingMore, setIsLoadingMore]   = useState(false);
  const [showDetails, setShowDetails]       = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);

  const fetchData = useCallback((cursor = null, append = false) => {
    if (!append) showSpinner();
    setIsLoadingMore(true);

    const payload = { pageSize: PAGE_SIZE };

    if (sort) {
      const [sortField, sortDir] = sort.split("=");
      if (sortField && sortDir) {
        payload.sorter = {
          [sortField === "triggeredAt" ? "createdAt" : sortField]: parseInt(sortDir, 10),
        };
      }
    }
    if (cursor) payload.lastSeenId = cursor;
    if (searchValue) payload.search = searchValue;

    getNotificationChannels(
      payload,
      (resStatus, responseString, resJson) => {
        if (!append) closeSpinner();
        setIsLoadingMore(false);

        if (resStatus === 401) {
          modal(sessionExpired);
          toScreen(false, navigate, "/logout");
        } else if (resStatus === "400") {
          if (!append) setData([]);
          alert(responseString);
        } else if (resStatus === 200 || resStatus === "200") {
          const fetched =
            resJson?.payload?.channels ||
            resJson?.payload?.data ||
            resJson?.payload ||
            resJson?.channels ||
            resJson?.data ||
            [];

          const nextCursorVal =
            resJson?.payload?.nextCursor ||
            resJson?.payload?.lastSeenId ||
            resJson?.nextCursor ||
            resJson?.lastSeenId ||
            null;
          const hasNextVal =
            resJson?.payload?.hasNextPage ||
            resJson?.payload?.hasMore ||
            resJson?.hasNextPage ||
            resJson?.hasMore ||
            false;

          if (Array.isArray(fetched) && fetched.length > 0) {
            const processed = fetched.map((item) => ({
              activityKey: item?.activityKey || item?.activity_key || "-",
              channel:     item?.channel || item?.name || "-",
              description: item?.description || "-",
              type:        item?.notificationType || "-",
              status:      item?.isActive ? "Active" : "Inactive",
              provider:    item?.provider || "-",
              isActive:    item?.isActive || false,
              createdAt:   item?.createdAt || "-",
              updatedAt:   item?.updatedAt || "-",
              _original:   item,
            }));
            if (append) setData((prev) => [...prev, ...processed]);
            else setData(processed);
            setNextCursor(nextCursorVal);
            setHasNextPage(hasNextVal);
          } else if (!append) {
            setData([]);
            setNextCursor(null);
            setHasNextPage(false);
          }
        }
      },
      userDetails.jwttoken,
    );
  }, [searchValue, sort, userDetails.jwttoken, navigate]);

  useEffect(() => {
    setData([]);
    setCursorStack([null]);
    setNextCursor(null);
    setHasNextPage(false);
    fetchData(null, false);
  }, [fetchData]);

  const handleTableScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollTop + clientHeight >= scrollHeight - 100 &&
      hasNextPage && nextCursor && !isLoadingMore
    ) {
      const newStack = [...cursorStack, nextCursor];
      setCursorStack(newStack);
      fetchData(nextCursor, true);
    }
  };

  const handlePreviousPage = () => {
    if (cursorStack.length > 1) {
      const newStack = cursorStack.slice(0, -1);
      setCursorStack(newStack);
      fetchData(newStack[newStack.length - 1], false);
    }
  };

  const handleNextPage = () => {
    if (hasNextPage && nextCursor && !isLoadingMore) {
      const newStack = [...cursorStack, nextCursor];
      setCursorStack(newStack);
      fetchData(nextCursor, false);
    }
  };

  const handleSearchSubmit = () => {
    setSearchValue(inputSearchValue);
    setCursorStack([null]);
    setNextCursor(null);
  };

  const sortData = (field) => {
    setCursorStack([null]);
    setNextCursor(null);
    setSort(sort === `${field}=1` ? `${field}=-1` : `${field}=1`);
  };

  const getSortClass = (key) => {
    if (sort === `${key}=1`) return "c-list-filter__button c-list-filter__button--icon-up";
    if (sort === `${key}=-1`) return "c-list-filter__button c-list-filter__button--icon-down";
    return "c-list-filter__button c-list-filter__button--faded";
  };

  const renderCell = (item, field) => {
if (field.key === "channel") {
  const channel = item[field.key];

  return (
    <span className={`badge ${CHANNEL_BADGE[channel] || "badge-secondary"}`}>
      {channel}
    </span>
  );
}
    if (field.key === "status")
      return <span className="badge badge-warning">{item[field.key]}</span>;
    if (field.key === "description") {
      const tooltipId = `ch-desc-${item.activityKey}`;
      return (
        <>
          <span data-tooltip-id={tooltipId} className="description-trigger">
            {item[field.key]}
          </span>
          <Tooltip id={tooltipId} content={item[field.key]} place="top" />
        </>
      );
    }
    return <span>{item[field.key]}</span>;
  };


  return (
    <>
              {showDetails && <div className="modal-backdrop"></div>}

      <div className="c-subheader-info-panel">
        <div className="c-subheader-info-panel__search">
          <form onSubmit={(e) => { e.preventDefault(); handleSearchSubmit(); }}>
            <input
              type="text"
              value={inputSearchValue}
              onChange={(e) => setInputSearchValue(e.target.value)}
              placeholder="Search channels..."
              className="c-subheader-info-panel__input"
            />
            <button
              type="submit"
              className="c-btn-outline c-btn-outline--white c-btn-outline--small"
            >
              Search
            </button>
          </form>
        </div>
      </div>

      <div className="c-hr"></div>

      <div className="l-row">
        <div className="l-col-100">
          <div className="c-hr u-margin-bottom-0"></div>

          <div className="notification-list-table">
            <div className="c-list-entry c-list-entry__header align-items-center">
              {COLUMNS.map((col) => (
                <div key={col.key} className={col.class}>
                  {col.sortable === false ? (
                    <span style={{ opacity: 0.6 }}>{col.label}</span>
                  ) : (
                    <div className="c-list-filter">
                      <button
                        className={getSortClass(col.key)}
                        onClick={() => sortData(col.key)}
                      >
                        {col.label}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="notification-scroll-list" onScroll={handleTableScroll}>
              {data.length > 0 ? (
                data.map((item, index) => (
                  <div className="c-list-entry align-items-center" key={index}>
                    {COLUMNS.map((field) => (
                      <div key={field.key} className={field.class}>
                        {renderCell(item, field)}
                      </div>
                    ))}
                  </div>
                ))
              ) : (
                <div className="u-text-align-center u-margin-top-bottom-m u-color-red">
                  No data found.
                </div>
              )}
              {isLoadingMore && (
                <div className="scroll-load-more">
                  <div className="scroll-load-more__spinner"></div>
                  <p className="scroll-load-more__text">Loading more...</p>
                </div>
              )}
            </div>
          </div>

          <div className="c-hr u-margin-bottom-0"></div>
        </div>
        <div style={{ clear: "both" }}></div>
      </div>

      {/* <div className="cursor-pagination">
        <button
          className="btn btn-outline-primary cursor-pagination__btn"
          onClick={handlePreviousPage}
          disabled={cursorStack.length <= 1}
        >
          ← Previous
        </button>
        <button
          className="btn btn-primary cursor-pagination__btn"
          onClick={handleNextPage}
          disabled={!hasNextPage || !nextCursor || isLoadingMore}
        >
          Next →
        </button>
      </div> */}

      {showDetails && (
        <ChannelDetails
          channelDetails={selectedChannel}
          onClose={() => { setShowDetails(false); setSelectedChannel(null); }}
        />
      )}
    </>
  );
};

ChannelsTab.propTypes = {
  userDetails: PropTypes.object.isRequired,
  navigate:    PropTypes.func.isRequired,
};

export default ChannelsTab;
